import React from "react";
import { Box, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { createSidebarResetState } from "../utils/sidebarNavigation";

const PURPLE = "#7C3AED";

export default function RpoBreadcrumb({
  items = [],
  activeLabel,
  appendActiveLabel = false,
  onNavigate, // optional global handler
}) {
  const location = useLocation();
  const safeItems = Array.isArray(items)
    ? items.filter((item) => String(item?.label || "").trim())
    : [];
  const normalizedActiveLabel = String(activeLabel || "").trim();
  const lastItemLabel = String(safeItems[safeItems.length - 1]?.label || "").trim();
  const shouldAppendActiveLabel =
    appendActiveLabel &&
    normalizedActiveLabel &&
    lastItemLabel.toLowerCase() !== normalizedActiveLabel.toLowerCase();
  const resolvedItems = shouldAppendActiveLabel
    ? [...safeItems, { label: normalizedActiveLabel }]
    : safeItems;

  if (!resolvedItems.length) return null;

  return (
    <Box
      className="breadcrumb"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        rowGap: 0.5,
        columnGap: 1,
        fontFamily: "Be Vietnam Pro",
        width: "100%",
        minWidth: 0,
        mb: 2,
      }}
    >
      {resolvedItems.map((item, index) => {
        const isLast = index === resolvedItems.length - 1;
        const isClickable =
          !isLast &&
          (typeof item?.onClick === "function" ||
            Boolean(item?.path) ||
            typeof onNavigate === "function");
        const handleClick = () => {
          if (typeof item?.onClick === "function") {
            item.onClick();
            return;
          }

          onNavigate?.(item);
        };
        const itemSearch = String(item?.search || "");
        const itemHash = String(item?.hash || "");
        const itemPath = String(item?.path || "");
        const isPathLink = Boolean(itemPath) && typeof item?.onClick !== "function";

        return (
          <React.Fragment key={index}>
            {/* First item gets arrow */}
            {index === 0 && (
              <ArrowBackIcon
                sx={{
                  fontSize: 18,
                  color: PURPLE,
                }}
              />
            )}

            {/* Only non-final breadcrumb items are clickable links */}
            {isClickable ? (
              <Typography
                component={isPathLink ? RouterLink : "button"}
                to={
                  isPathLink
                    ? { pathname: itemPath, search: itemSearch, hash: itemHash }
                    : undefined
                }
                replace={
                  isPathLink
                    ? Boolean(item?.replace) ||
                      (location.pathname === itemPath &&
                        location.search === itemSearch &&
                        location.hash === itemHash)
                    : undefined
                }
                state={
                  isPathLink
                    ? item?.state ?? createSidebarResetState(itemPath)
                    : undefined
                }
                onClick={isPathLink ? undefined : handleClick}
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: PURPLE,
                  cursor: "pointer",
                  border: 0,
                  background: "transparent",
                  p: 0,
                  textDecoration: "none",
                  fontFamily: "inherit",
                  maxWidth: "100%",
                  overflowWrap: "anywhere",
                  transition: "opacity 0.2s ease",
                  "&:hover": {
                    opacity: 0.7,
                  },
                }}
              >
                {item.label}
              </Typography>
            ) : (
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#6B7280",
                  maxWidth: "100%",
                  overflowWrap: "anywhere",
                }}
              >
                {item.label}
              </Typography>
            )}

            {/* Divider except last */}
            {!isLast && (
              <Box
                sx={{
                  height: 18,
                  width: "1px",
                  backgroundColor: "#D1D5DB",
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}
